import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Check, AlertCircle } from 'lucide-react';
import { useWhatsAppGlobalConfig } from '@/hooks/useWhatsAppGlobalConfig';
import { Alert, AlertDescription } from '@/components/ui/alert';

export const WhatsAppGlobalSettings = () => {
  const { config, loading, saveConfig, testConnection } = useWhatsAppGlobalConfig();
  const [apiUrl, setApiUrl] = useState(config?.evolution_api_url || '');
  const [apiKey, setApiKey] = useState(config?.evolution_api_key || '');
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);

  const handleTestConnection = async () => {
    if (!apiUrl || !apiKey) {
      return;
    }

    setIsTesting(true);
    setTestSuccess(false);
    const success = await testConnection(apiUrl, apiKey);
    setTestSuccess(success);
    setIsTesting(false);
  };

  const handleSaveConfig = async () => {
    if (!apiUrl || !apiKey) {
      return;
    }

    setIsSaving(true);
    try {
      await saveConfig(apiUrl, apiKey);
    } catch (error) {
      console.error('Error saving config:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configuração Global do WhatsApp</CardTitle>
        <CardDescription>
          Configure a Evolution API uma vez. Todos os vendedores usarão esta configuração.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Esta configuração é compartilhada por todos os usuários. Apenas administradores podem modificá-la.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="apiUrl">Evolution API URL</Label>
            <Input
              id="apiUrl"
              placeholder="https://evolution-api-production-xxxx.up.railway.app"
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              URL completa do seu servidor Evolution API (com https://)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="apiKey">API Key</Label>
            <Input
              id="apiKey"
              type="password"
              placeholder="Sua API Key do Evolution"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              A chave de API configurada no Evolution (AUTHENTICATION_API_KEY)
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleTestConnection}
              disabled={!apiUrl || !apiKey || isTesting}
              variant="outline"
              className="flex-1"
            >
              {isTesting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testando...
                </>
              ) : testSuccess ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Testado
                </>
              ) : (
                'Testar Conexão'
              )}
            </Button>

            <Button
              onClick={handleSaveConfig}
              disabled={!apiUrl || !apiKey || isSaving || !testSuccess}
              className="flex-1"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar Configuração'
              )}
            </Button>
          </div>

          {config && (
            <Alert className="bg-green-50 border-green-200">
              <Check className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Configuração ativa desde {new Date(config.created_at).toLocaleDateString('pt-BR')}
              </AlertDescription>
            </Alert>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
